/**
 * Dashboard Types
 *
 * Story 4.1: Dashboard Analytics & Performance Overview
 *
 * TypeScript interfaces and Zod schemas for the dashboard page,
 * including performance metrics, AI suggestions, hot leads, and system status.
 */
import { z } from 'zod';
export const WeeklyPerformanceDaySchema = z.object({
    date: z.string(),
    dayOfWeek: z.enum(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']),
    revenue: z.number().min(0),
    target: z.number().min(0),
});
export const WeeklyPerformanceResponseSchema = z.object({
    days: z.array(WeeklyPerformanceDaySchema),
    totalRevenue: z.number().min(0),
    totalTarget: z.number().min(0),
    percentOfTarget: z.number().min(0).max(100),
});
export const MonthlyPerformanceWeekSchema = z.object({
    weekNumber: z.number().min(1).max(5),
    weekStart: z.string(),
    revenue: z.number().min(0),
    target: z.number().min(0),
});
export const MonthlyPerformanceResponseSchema = z.object({
    weeks: z.array(MonthlyPerformanceWeekSchema),
    totalRevenue: z.number().min(0),
    totalTarget: z.number().min(0),
    percentOfTarget: z.number().min(0).max(100),
});
export const QuarterlyPerformancePointSchema = z.object({
    quarter: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    label: z.enum(['Q1', 'Q2', 'Q3', 'Q4']),
    quarterStart: z.string(),
    revenue: z.number().min(0),
    target: z.number().min(0),
});
export const QuarterlyPerformanceResponseSchema = z.object({
    quarters: z.array(QuarterlyPerformancePointSchema),
    totalRevenue: z.number().min(0),
    totalTarget: z.number().min(0),
    percentOfTarget: z.number().min(0).max(100),
});
// =============================================================================
// AI Suggestion Types
// =============================================================================
/**
 * AI suggestion categories for dashboard display
 */
export const AISuggestionCategory = {
    COACHING: 'coaching',
    DEAL: 'deal',
    TASK: 'task',
    MEETING: 'meeting',
};
export const AISuggestionCategorySchema = z.enum(Object.values(AISuggestionCategory));
/**
 * Related entity types for AI suggestions
 */
export const RelatedEntityType = {
    MEETING: 'meeting',
    DEAL: 'deal',
    CLIENT: 'client',
    TASK: 'task',
};
export const RelatedEntityTypeSchema = z.enum(Object.values(RelatedEntityType));
export const AISuggestionResponseSchema = z.object({
    suggestionText: z.string().min(1),
    category: AISuggestionCategorySchema,
    relatedEntityId: z.string().nullable(),
    relatedEntityType: RelatedEntityTypeSchema.nullable(),
    generatedAt: z.string().datetime(),
});
// =============================================================================
// AI Briefing Types (Smart Dashboard Panel)
// =============================================================================
/**
 * Urgency classification for briefing deal items
 */
export const BriefingDealUrgency = {
    CRITICAL: 'critical',
    HIGH: 'high',
    MEDIUM: 'medium',
};
export const BriefingDealUrgencySchema = z.enum(Object.values(BriefingDealUrgency));
export const BriefingDealItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    value: z.number(),
    client: z.string(),
    clientId: z.string().nullable(),
    urgency: BriefingDealUrgencySchema,
    reason: z.string(),
    type: z.enum(['at_risk', 'closing_soon']),
});
export const AIBriefingResponseSchema = z.object({
    insightText: z.string().min(1),
    category: AISuggestionCategorySchema,
    deals: z.array(BriefingDealItemSchema),
    generatedAt: z.string().datetime(),
});
export const HotLeadsFilterParamsSchema = z.object({
    stage: z.string().optional(),
    minValue: z.number().min(0).optional(),
    maxValue: z.number().min(0).optional(),
    ownerId: z.string().optional(),
});
// =============================================================================
// System Status Types
// =============================================================================
/**
 * System connection status types
 */
export const SystemStatusType = {
    ONLINE: 'online',
    LIMITED: 'limited',
    OFFLINE: 'offline',
};
export const SystemStatusTypeSchema = z.enum(Object.values(SystemStatusType));
export const SystemStatusSchema = z.object({
    status: SystemStatusTypeSchema,
    lastSyncAt: z.string().datetime().nullable(),
    apiHealthy: z.boolean(),
    wsConnected: z.boolean(),
});
export const DashboardKPIsSchema = z.object({
    totalRevenue: z.number().min(0),
    revenueChange: z.number(),
    totalDeals: z.number().min(0),
    dealsChange: z.number(),
    meetingsToday: z.number().min(0),
    meetingsThisWeek: z.number().min(0),
    pipelineValue: z.number().min(0),
    pipelineChange: z.number(),
});
export const DashboardResponseSchema = z.object({
    kpis: DashboardKPIsSchema,
    weeklyPerformance: WeeklyPerformanceResponseSchema,
    monthlyPerformance: MonthlyPerformanceResponseSchema,
    aiSuggestion: AISuggestionResponseSchema.nullable(),
    systemStatus: SystemStatusSchema,
});
export const DashboardQuerySchema = z.object({
    period: z.enum(['week', 'month', 'quarter']).optional(),
    userId: z.string().uuid().optional(),
});
export const PerformanceQuerySchema = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    granularity: z.enum(['day', 'week', 'month']).optional(),
});
// =============================================================================
// Dashboard Preset Types (Customizable Dashboard Grid)
// =============================================================================
export const WidgetPeriod = {
    MONTHLY: 'monthly',
    QUARTERLY: 'quarterly',
    YEARLY: 'yearly',
};
